"use client";

import type { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import * as React from "react";

import { useIsTouchDevice } from "./use-is-touch-device";

type InitialFocusResolver = Extract<
  NonNullable<PopoverPrimitive.Popup.Props["initialFocus"]>,
  (...args: never[]) => unknown
>;

/** How a popup was opened, as Base UI reports it to `initialFocus`. */
export type OpenInteractionType = Parameters<InitialFocusResolver>[0];

/**
 * Focus follows the promise of the press.
 *
 * A press ON text — a value cell, a Rename action, a compose button, a
 * create dialog whose first field is the name — promised a keyboard, and
 * that field takes focus on every device: those sites pass `autoFocus`
 * themselves. A press that opened a CONTAINER where typing is optional — a
 * picker, an options panel, a menu with a filter row, a dialog whose search
 * heads a list — seats the caret only where a hardware keyboard is implied.
 * On a coarse pointer the field renders and waits for its own tap: the soft
 * keyboard would cover half the list it narrows, and a list on touch is
 * scanned by scrolling. A wanted search costs one tap.
 *
 * The axis is pointer capability, never viewport width. An iPad in
 * landscape is a desktop layout with no hardware keyboard; an iPad with a
 * trackpad reads as a fine pointer and keeps desktop focus, and its soft
 * keyboard stays down with hardware attached.
 *
 * Resolves the default for an optional-typing field's `autoFocus`. An
 * explicit value wins — the caller knows when the press promised typing.
 */
export function useOptionalAutoFocus(autoFocus?: boolean): boolean {
  const touch = useIsTouchDevice();
  return autoFocus ?? !touch;
}

/**
 * Base UI focuses a popup's first tabbable element on open unless the open
 * was a touch, when it focuses the popup itself so no keyboard rises. It
 * reads the touch off the trigger's click, and WebKit stamps a finger's
 * click `pointerType: "mouse"`, so on an iPad the guard misses and a
 * popup's search field takes the keyboard. This hands Base UI the device's
 * pointer capability as the second witness: a keyboard open still lands on
 * the first tabbable; any other open on a coarse pointer lands on the popup.
 * A field that already claimed focus on mount by its own `autoFocus` — the
 * press promised typing — is left holding it.
 *
 * Returns the ref to put on the popup (merged with the caller's) and the
 * resolver to pass as `initialFocus`.
 */
export function useTouchAwareInitialFocus<T extends HTMLElement>(
  forwardedRef: React.Ref<T> | undefined,
): {
  ref: React.RefCallback<T>;
  initialFocus: (openType: OpenInteractionType) => T | boolean;
} {
  const touch = useIsTouchDevice();
  const popupRef = React.useRef<T | null>(null);

  const ref = React.useCallback(
    (node: T | null) => {
      popupRef.current = node;
      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    },
    [forwardedRef],
  );

  const initialFocus = React.useCallback(
    (openType: OpenInteractionType): T | boolean => {
      const popup = popupRef.current;
      if (!popup) return true;
      const active = popup.ownerDocument.activeElement;
      // `false`: leave focus where it is.
      if (active && active !== popup && popup.contains(active)) return false;
      if (openType === "keyboard") return true;
      if (openType === "touch" || touch) return popup;
      return true;
    },
    [touch],
  );

  return { initialFocus, ref };
}
